/**
 * Unified configuration for pages that should not be translated/localized.
 *
 * This is the SINGLE SOURCE OF TRUTH for:
 * 1. Preventing language prefix in URLs (useBaseLink)
 * 2. Excluding international hreflang tags (HeadComponent)
 * 3. Hiding language switch popup (GlobalMain)
 *
 * Two matching modes:
 * - PREFIX: Matches any path starting with the specified prefix (e.g., '/resources' matches '/resources/article-slug')
 * - EXACT: Matches only the exact path (e.g., '/privacy-policy' only matches '/privacy-policy')
 */

import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, Language } from './i18n-config';

export type MatchMode = 'prefix' | 'exact';

export interface UntranslatedPageConfig {
  path: string;
  matchMode: MatchMode;
  description?: string;
}

export const UNTRANSLATED_PAGES_CONFIG: UntranslatedPageConfig[] = [
  // Content sections with sub-pages (prefix matching)
  { path: '/resources', matchMode: 'prefix', description: 'Resource Center and all articles' },
  { path: '/case-studies', matchMode: 'prefix', description: 'Case Studies and all articles' },
  { path: '/momentum', matchMode: 'prefix', description: 'Momentum newsletter and all articles' },
  { path: '/encyclopedia', matchMode: 'prefix', description: 'Encyclopedia and all articles' },
  { path: '/job', matchMode: 'prefix', description: 'Job listings' },
  { path: '/bodi', matchMode: 'prefix', description: 'BODi communities' },
  { path: '/experts-directory', matchMode: 'prefix', description: 'Experts directory and profiles' },

  // Legal pages (exact matching)
  { path: '/privacy-policy', matchMode: 'exact', description: 'Privacy Policy' },
  { path: '/terms-of-use', matchMode: 'exact', description: 'Terms of Use' },
  { path: '/accessibility', matchMode: 'exact', description: 'Accessibility Statement' },
];

/**
 * Checks if a given path should be treated as untranslated.
 * Handles both prefix and exact matching based on configuration.
 *
 * @param pathname - The pathname to check (e.g., '/resources/some-article' or '/es/resources/some-article')
 * @returns boolean - True if the path is untranslated
 */
export const isUntranslatedPath = (pathname: string): boolean => {
  if (!pathname) return false;

  // Normalize the path: remove trailing slash
  let normalizedPath = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;

  // Strip language prefix if present (e.g., /es/resources -> /resources)
  const pathParts = normalizedPath.split('/').filter(Boolean);
  const potentialLang = pathParts[0] as Language;

  // Check if first segment is a non-default language
  if (SUPPORTED_LANGUAGES.includes(potentialLang) && potentialLang !== DEFAULT_LANGUAGE) {
    normalizedPath = '/' + pathParts.slice(1).join('/') || '/';
  }

  return UNTRANSLATED_PAGES_CONFIG.some((config) => {
    if (config.matchMode === 'exact') {
      return normalizedPath === config.path;
    }
    // prefix matching - matches exact path or path with additional segments
    return normalizedPath === config.path || normalizedPath.startsWith(`${config.path}/`);
  });
};
